import React from 'react'
import { useSearchParams } from 'react-router-dom'
import { VideoCameraIcon, DocumentTextIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { getYearOptions, exportToCsv } from '../../utils/registryExport'

/** Join list or single value for display */
function smrFormatList(val) {
  if (val == null || val === '') return ''
  if (Array.isArray(val)) return val.filter(Boolean).map(String).join(', ')
  return String(val)
}

/**
 * Submitted SMR payloads from getSmrDraft() use nested AUSTRAC keys (grounds, partB, partH, …).
 * Older rows may use flat subject / narrative / reason_for_suspicion. Normalize for the registry UI.
 */
function normalizeSmrSubmissionData(d) {
  if (!d || typeof d !== 'object') return null
  const grounds = d.grounds || {}
  const partB = d.partB || {}
  const partG = d.partG || {}
  const suspiciousPerson = d.suspiciousPerson || {}
  const partC = d.partC || {}
  const primary = partC.primary || {}

  const narrative =
    d.narrative ||
    grounds.B1_narrative ||
    partB.groundsForSuspicion ||
    d.grounds_for_suspicion ||
    ''
  const nature = d.nature || partB.nature || ''
  const subject =
    d.subject ||
    d.meta?.internalReference ||
    d.meta?.caseNumber ||
    d.partH?.internalReference ||
    ''

  const suspicionArr =
    (Array.isArray(d.suspicion_reasons) && d.suspicion_reasons.length ? d.suspicion_reasons : null) ||
    (Array.isArray(partB.suspReason) ? partB.suspReason : null) ||
    []
  const reasonOther = partB.suspReasonOther || ''

  const offenceFromGrounds = grounds.B2_offenceType
  const offenceArr =
    (Array.isArray(d.offence_types) && d.offence_types.length ? d.offence_types : null) ||
    (Array.isArray(offenceFromGrounds) ? offenceFromGrounds : offenceFromGrounds ? [offenceFromGrounds] : null) ||
    (partG.offenceType ? [partG.offenceType] : []) ||
    []

  const designatedService = grounds.B5_designatedService || ''
  const personName =
    (suspiciousPerson.C3_givenNames || suspiciousPerson.C2_familyName)
      ? `${suspiciousPerson.C3_givenNames || ''} ${suspiciousPerson.C2_familyName || ''}`.replace(/\s+/g, ' ').trim()
      : primary.fullName || ''

  const transactions = Array.isArray(d.transactions) ? d.transactions : d.partF?.transactions
  const txnCount = Array.isArray(transactions) ? transactions.length : null

  const reasonForSuspicion =
    d.reason_for_suspicion ||
    (suspicionArr.length ? smrFormatList(suspicionArr) : '') ||
    reasonOther ||
    ''

  return {
    subject: subject || null,
    nature: nature || null,
    narrative: narrative || null,
    reason_for_suspicion: reasonForSuspicion || null,
    suspicion_reasons: suspicionArr.length ? suspicionArr : null,
    offence_types: offenceArr.length ? offenceArr : null,
    designated_service: designatedService || null,
    person_summary: personName || suspiciousPerson.C20_memberNumber || null,
    transaction_count: txnCount,
  }
}

function smrNormalizedHasContent(n) {
  if (!n) return false
  return !!(
    n.subject ||
    n.nature ||
    n.narrative ||
    n.reason_for_suspicion ||
    (n.suspicion_reasons && n.suspicion_reasons.length) ||
    (n.offence_types && n.offence_types.length) ||
    n.designated_service ||
    n.person_summary ||
    n.transaction_count != null
  )
}

export default function SMR_ECDD_Registry() {
  const [searchParams, setSearchParams] = useSearchParams()
  const caseIdFromUrl = searchParams.get('caseId')
  const yearFromUrl = searchParams.get('year') || ''
  const [items, setItems] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const [venues, setVenues] = React.useState([])
  const [filters, setFilters] = React.useState({ member_id: '', search: '', venue_id: '', year: yearFromUrl || '' })
  const [previewCase, setPreviewCase] = React.useState(null)
  const [previewSubmission, setPreviewSubmission] = React.useState(null)
  const [previewPreviousSmrs, setPreviewPreviousSmrs] = React.useState([])
  const [previewEvidence, setPreviewEvidence] = React.useState(null)
  const [previewAttachments, setPreviewAttachments] = React.useState([])
  const [previewLoading, setPreviewLoading] = React.useState(false)
  const [previewSubmissionError, setPreviewSubmissionError] = React.useState(null)
  const [registryHint, setRegistryHint] = React.useState(null)

  React.useEffect(() => {
    authFetch(getLocalApiUrl('/api/venues'))
      .then(r => r.json())
      .then(j => { if (j.success) setVenues(j.data || []) })
      .catch(() => {})
  }, [])

  // Sync year from URL on mount
  React.useEffect(() => {
    if (yearFromUrl && yearFromUrl !== filters.year) {
      setFilters(prev => ({ ...prev, year: yearFromUrl }))
    }
  }, [yearFromUrl])

  // Sync year to URL when filter changes
  const setYear = (y) => {
    setFilters(prev => ({ ...prev, year: y }))
    const next = new URLSearchParams(searchParams)
    if (y) next.set('year', y)
    else next.delete('year')
    setSearchParams(next, { replace: true })
  }

  const load = React.useCallback(async () => {
    try {
      setLoading(true); setError(null)
      const qs = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([, v]) => v)))
      const url = getLocalApiUrl(`/api/registry/forms?${qs}&limit=500`)
      const r = await authFetch(url)
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Failed to load registry')
      setItems(j.data || [])
      setRegistryHint(j.hint || null)
    } catch (e) { setError(e.message) } finally { setLoading(false) }
  }, [filters])

  React.useEffect(() => { load() }, [load])

  const handleExport = () => {
    const cols = [
      { header: 'Member ID', accessor: r => r.member_id },
      { header: 'Member Name', accessor: r => r.member_name || '' },
      { header: 'Case Number', accessor: r => r.case_number || r.case_id },
      { header: 'Venue', accessor: r => r.venue_name ? `${r.venue_name}${r.venue_code ? ` (${r.venue_code})` : ''}` : '' },
      { header: 'Severity', accessor: r => r.severity || '' },
      { header: 'Filed', accessor: r => r.last_submitted_at ? new Date(r.last_submitted_at).toLocaleString() : '' },
      { header: 'Submitted By', accessor: r => r.submitted_by || '' },
    ]
    const suffix = filters.year ? `-${filters.year}` : ''
    exportToCsv(items, cols, `smr-registry${suffix}.csv`)
  }

  // Auto-open preview when navigated with ?caseId=
  React.useEffect(() => {
    if (!caseIdFromUrl || items.length === 0 || previewCase) return
    const row = items.find(r => (r.case_id && String(r.case_id) === caseIdFromUrl) || (r.case_number && String(r.case_number) === caseIdFromUrl))
    if (row) {
      openPreview(row)
      const next = new URLSearchParams(searchParams)
      next.delete('caseId')
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseIdFromUrl, items, previewCase])

  const openPreview = async (row) => {
    setPreviewCase(row)
    setPreviewSubmission(null)
    setPreviewPreviousSmrs([])
    setPreviewEvidence(null)
    setPreviewAttachments([])
    setPreviewSubmissionError(null)
    setPreviewLoading(true)
    try {
      // Never do `${getLocalApiUrl('/api/cases/ID')}/subpath` — getLocalApiUrl adds ?tenant=… so that becomes
      // /api/cases/ID?tenant=tsg/subpath → browser requests /api/cases/ID only (4× getCase, data is object not array).
      const cid = encodeURIComponent(row.case_id)
      const [subRes, prevRes, evRes, attRes] = await Promise.all([
        authFetch(getLocalApiUrl(`/api/cases/${cid}/forms/SMR/submissions`)).then(r => r.json()),
        authFetch(getLocalApiUrl(`/api/cases/${cid}/previous-smrs`)).then(r => r.json()),
        authFetch(getLocalApiUrl(`/api/cases/${cid}/evidence?page=1&limit=50`)).then(r => r.json()),
        authFetch(getLocalApiUrl(`/api/cases/${cid}/attachments`)).then(r => r.json()).catch(() => ({ success: false, data: [] })),
      ])
      const rawList = subRes.success ? subRes.data : null
      const listIsArray = Array.isArray(rawList)
      const submissions = listIsArray ? rawList : []
      if (subRes.success && rawList != null && !listIsArray) {
        setPreviewSubmission(null)
        setPreviewSubmissionError('Submissions API returned non-array data; cannot resolve filing id. Check API / network.')
      } else {
        const latest = submissions[0]
        const submissionId = latest != null ? (latest.id ?? latest.submission_id) : null
        if (submissionId != null && submissionId !== '') {
          const detailUrl = getLocalApiUrl(
            `/api/cases/${encodeURIComponent(row.case_id)}/forms/SMR/submissions/${encodeURIComponent(String(submissionId))}`
          )
          const detailRes = await authFetch(detailUrl)
          let detailJson = null
          try {
            detailJson = await detailRes.json()
          } catch {
            setPreviewSubmission(null)
            setPreviewSubmissionError('Invalid response when loading submission.')
          }
          if (detailJson == null) {
            setPreviewSubmission(null)
            if (!detailRes.ok) {
              setPreviewSubmissionError(`Could not load submission (HTTP ${detailRes.status}).`)
            } else {
              setPreviewSubmissionError('Submission detail response was empty or not JSON.')
            }
          } else if (!detailRes.ok) {
            setPreviewSubmission(null)
            setPreviewSubmissionError(detailJson.error || `Could not load submission (HTTP ${detailRes.status}).`)
          } else if (detailJson.success && detailJson.data) {
            setPreviewSubmission(detailJson.data)
          } else {
            setPreviewSubmission(null)
            setPreviewSubmissionError(detailJson.error || 'Could not load submission details.')
          }
        } else if (subRes.success && submissions.length === 0) {
          setPreviewSubmission(null)
          setPreviewSubmissionError('No SMR submission row found for this case (registry may be out of sync).')
        } else if (subRes.success && submissions.length > 0) {
          setPreviewSubmission(null)
          setPreviewSubmissionError('Latest submission has no id; cannot load details.')
        } else if (!subRes.success) {
          setPreviewSubmission(null)
          setPreviewSubmissionError(subRes.error || 'Failed to list submissions.')
        }
      }
      if (prevRes.success && Array.isArray(prevRes.data)) setPreviewPreviousSmrs(prevRes.data)
      if (evRes.success && evRes.data) setPreviewEvidence(evRes.data)
      const atts = attRes.success && Array.isArray(attRes.data)
        ? attRes.data.map(a => ({ id: a.id, name: a.file_name, type: (a.mime_type || '').startsWith('video/') ? 'video' : 'document', mime: a.mime_type, size: a.size_bytes ? `${(a.size_bytes / 1024).toFixed(1)} KB` : '—', uploadedAt: a.uploaded_at, description: null }))
        : []
      setPreviewAttachments(atts)
    } catch (e) {
      setPreviewSubmission(null)
      setPreviewSubmissionError(e?.message || 'Failed to load SMR details.')
    } finally { setPreviewLoading(false) }
  }
  const closePreview = () => {
    setPreviewCase(null)
    setPreviewSubmission(null)
    setPreviewSubmissionError(null)
    setPreviewPreviousSmrs([])
    setPreviewEvidence(null)
    setPreviewAttachments([])
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="card mb-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">SMR Registry</h1>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">{loading ? 'Loading…' : `${items.length} filed SMR${items.length !== 1 ? 's' : ''}`}</span>
            <button type="button" onClick={handleExport} className="btn-secondary flex items-center gap-1.5 px-3 py-1.5 text-sm" disabled={loading || items.length === 0}>
              <ArrowDownTrayIcon className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-1">Suspicious Matter Reports that have been filed and submitted to AUSTRAC. 7-year retention for reporting.</p>
        <div className="mt-3 grid grid-cols-5 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Year</label>
            <select className="select-field w-full" value={filters.year} onChange={(e) => setYear(e.target.value)}>
              <option value="">All years</option>
              {getYearOptions().map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Venue</label>
            <select className="select-field w-full" value={filters.venue_id} onChange={(e) => setFilters(prev => ({ ...prev, venue_id: e.target.value }))}>
              <option value="">All Venues</option>
              {venues.map(v => (
                <option key={v.id} value={v.id}>{v.name} ({v.code})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Member ID</label>
            <input className="input-field w-full" value={filters.member_id} onChange={(e) => setFilters(prev => ({ ...prev, member_id: e.target.value }))} placeholder="Filter by member" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs text-gray-600 mb-1">Search</label>
            <input className="input-field w-full" placeholder="Case #, member name, case ID, venue" value={filters.search} onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))} />
          </div>
        </div>
      </div>
      <div className="card">
        {error && <div className="text-sm text-red-600 mb-2">{error}</div>}
        {registryHint && (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3" role="status">
            {registryHint}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Member</th>
                <th className="px-3 py-2 text-left">Case</th>
                <th className="px-3 py-2 text-left">Venue</th>
                <th className="px-3 py-2 text-left">Report Status</th>
                <th className="px-3 py-2 text-left">Severity</th>
                <th className="px-3 py-2 text-left">Filed</th>
                <th className="px-3 py-2 text-left">Submitted By</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {items.map(row => (
                <tr key={row.case_id}>
                  <td className="px-3 py-2 font-mono">{row.member_id}<div className="text-xs text-gray-500 font-sans">{row.member_name || '—'}</div></td>
                  <td className="px-3 py-2 font-mono">{row.case_number || row.case_id}</td>
                  <td className="px-3 py-2">{row.venue_name ? `${row.venue_name}${row.venue_code ? ` (${row.venue_code})` : ''}` : '—'}</td>
                  <td className="px-3 py-2"><span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Filed</span></td>
                  <td className="px-3 py-2">{row.severity ? <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${row.severity === 'CRITICAL' ? 'bg-red-100 text-red-800' : row.severity === 'HIGH' ? 'bg-orange-100 text-orange-800' : row.severity === 'MEDIUM' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-800'}`}>{row.severity}</span> : '—'}</td>
                  <td className="px-3 py-2">{row.last_submitted_at ? new Date(row.last_submitted_at).toLocaleString() : '—'}</td>
                  <td className="px-3 py-2 text-gray-600">{row.submitted_by || '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <button className="btn-secondary" onClick={() => openPreview(row)}>View</button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && !loading && (
                <tr><td className="px-3 py-8 text-gray-500 text-center" colSpan={8}>No filed SMRs. SMRs appear here once they have been submitted.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {previewCase && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/40 overflow-y-auto" onClick={closePreview}>
          <div className="w-full max-w-5xl bg-white rounded-lg shadow-xl my-6 max-h-[95vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b flex items-center justify-between flex-shrink-0">
              <div>
                <div className="text-xs text-gray-500">Filed SMR</div>
                <div className="text-xl font-semibold">{previewCase.case_number || `CASE-${previewCase.case_id}`}</div>
              </div>
              <button className="btn-secondary" onClick={closePreview}>Close</button>
            </div>
            <div className="p-5 overflow-y-auto flex-1">
              {previewLoading ? (
                <div className="py-12 text-center text-gray-500">Loading SMR details…</div>
              ) : (
                <div className="space-y-6">
                  {/* Venue & Member info */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="card p-4">
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Venue</div>
                      <div className="text-sm font-medium">{previewCase.venue_name || '—'}</div>
                      {previewCase.venue_code && <div className="text-xs text-gray-500 mt-0.5">{previewCase.venue_code}</div>}
                    </div>
                    <div className="card p-4">
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Member</div>
                      <div className="font-mono text-sm">{previewCase.member_id}</div>
                      <div className="text-xs text-gray-600 mt-0.5">{previewCase.member_name || '—'}</div>
                    </div>
                    <div className="card p-4">
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Filed</div>
                      <div className="text-sm">{previewCase.last_submitted_at ? new Date(previewCase.last_submitted_at).toLocaleString() : '—'}</div>
                    </div>
                    <div className="card p-4">
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Submitted By</div>
                      <div className="text-sm">{previewCase.submitted_by || '—'}</div>
                    </div>
                  </div>

                  {/* SMR Details (as filed) — flat legacy keys + AUSTRAC draft shape from getSmrDraft */}
                  {previewSubmissionError && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm px-4 py-3">{previewSubmissionError}</div>
                  )}
                  {previewSubmission != null && (() => {
                    const raw = previewSubmission.data
                    const payload = raw != null && typeof raw === 'object' ? raw : null
                    const smr = normalizeSmrSubmissionData(payload)
                    const showGrid = smrNormalizedHasContent(smr)
                    const jsonForPre = payload != null ? payload : previewSubmission
                    return (
                      <div className="card p-5">
                        <div className="text-sm font-semibold text-gray-800 border-b pb-3 mb-4">SMR Details (as filed)</div>
                        {raw == null && (
                          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded px-3 py-2 mb-3">
                            No JSON payload on this submission row (check DB <code className="text-xs">case_form_submissions.data</code>).
                          </div>
                        )}
                        {showGrid && (
                          <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {smr.subject && (
                                <div><div className="text-xs text-gray-500">Subject / reference</div><div className="text-sm">{smr.subject}</div></div>
                              )}
                              {smr.person_summary && (
                                <div><div className="text-xs text-gray-500">Subject person</div><div className="text-sm">{smr.person_summary}</div></div>
                              )}
                              {smr.designated_service && (
                                <div><div className="text-xs text-gray-500">Designated service</div><div className="text-sm">{smr.designated_service}</div></div>
                              )}
                              {smr.reason_for_suspicion && (
                                <div className="md:col-span-2"><div className="text-xs text-gray-500">Reason for suspicion</div><div className="text-sm">{smr.reason_for_suspicion}</div></div>
                              )}
                              {smr.nature && (
                                <div><div className="text-xs text-gray-500">Nature</div><div className="text-sm">{smr.nature}</div></div>
                              )}
                              {smr.suspicion_reasons?.length > 0 && (
                                <div className="md:col-span-2"><div className="text-xs text-gray-500">Suspicion reasons</div><div className="text-sm">{smr.suspicion_reasons.join(', ')}</div></div>
                              )}
                              {smr.offence_types?.length > 0 && (
                                <div className="md:col-span-2"><div className="text-xs text-gray-500">Offence types</div><div className="text-sm">{smr.offence_types.join(', ')}</div></div>
                              )}
                              {smr.transaction_count != null && (
                                <div><div className="text-xs text-gray-500">Transactions in report</div><div className="text-sm">{smr.transaction_count}</div></div>
                              )}
                            </div>
                            {smr.narrative && (
                              <div className="mt-4 space-y-3">
                                <div><div className="text-xs text-gray-500">Narrative / grounds (Part B)</div><div className="text-sm whitespace-pre-wrap">{smr.narrative}</div></div>
                              </div>
                            )}
                          </>
                        )}
                        {!showGrid && raw != null && (
                          <div className="text-sm text-gray-500 mb-3">No mapped summary fields; expand raw JSON below.</div>
                        )}
                        <details className="mt-4 border-t pt-3">
                          <summary className="text-xs font-medium text-gray-600 cursor-pointer select-none">Full submission JSON</summary>
                          <pre className="mt-2 text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-auto max-h-80 whitespace-pre-wrap break-words">{JSON.stringify(jsonForPre, null, 2)}</pre>
                        </details>
                      </div>
                    )
                  })()}
                  {!previewLoading && previewSubmission == null && !previewSubmissionError && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 text-gray-700 text-sm px-4 py-3">
                      Submission record did not load. Hard-refresh the page (ensure the API has the submission list fix), then try View again.
                    </div>
                  )}

                  {/* Previous SMRs */}
                  {previewPreviousSmrs.length > 0 && (
                    <div className="card p-5">
                      <div className="text-sm font-semibold text-gray-800 border-b pb-3 mb-4">Previous SMRs (same member)</div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead><tr className="border-b text-gray-500 text-xs uppercase"><th className="text-left py-2">Case</th><th className="text-left py-2">Type</th><th className="text-left py-2">Status</th><th className="text-left py-2">Created</th><th className="text-left py-2">Regulatory Ref</th></tr></thead>
                          <tbody>
                            {previewPreviousSmrs.map(r => (
                              <tr key={r.id} className="border-b border-gray-100">
                                <td className="py-2 font-mono">{r.case_number || r.id}</td>
                                <td className="py-2">{r.case_type || '—'}</td>
                                <td className="py-2">{r.status || '—'}</td>
                                <td className="py-2">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                                <td className="py-2 text-gray-600">{r.regulatory_ref || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Evidence Attachments */}
                  {previewAttachments.length > 0 && (
                    <div className="card p-5">
                      <div className="text-sm font-semibold text-gray-800 border-b pb-3 mb-4">Evidence Attachments</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {previewAttachments.map(att => (
                          <div key={att.id} className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition">
                            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center">
                              {att.type === 'video' ? (
                                <VideoCameraIcon className="w-5 h-5 text-red-600" />
                              ) : (
                                <DocumentTextIcon className="w-5 h-5 text-blue-600" />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 truncate">{att.name}</div>
                              {att.description && <div className="text-xs text-gray-500 mt-0.5">{att.description}</div>}
                              <div className="text-xs text-gray-400 mt-1">{att.size} • {att.uploadedAt ? new Date(att.uploadedAt).toLocaleString() : ''}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Evidence */}
                  {previewEvidence?.items?.length > 0 && (
                    <div className="card p-5">
                      <div className="text-sm font-semibold text-gray-800 border-b pb-3 mb-4">Evidence ({previewEvidence.items.length} items)</div>
                      <div className="overflow-x-auto max-h-64">
                        <table className="min-w-full text-sm">
                          <thead><tr className="border-b text-gray-500 text-xs uppercase"><th className="text-left py-2">Type</th><th className="text-left py-2">Date/Time</th><th className="text-left py-2">Venue</th><th className="text-left py-2">Device</th><th className="text-right py-2">Cash In</th><th className="text-right py-2">Cash Out</th><th className="text-right py-2">Turnover</th></tr></thead>
                          <tbody>
                            {previewEvidence.items.slice(0, 30).map((it, idx) => {
                              const isSession = !!(it.SessionPK || it.StartTime)
                              const date = it.SnapshotTime || it.TransactionTime || it.StartTime || it.timestamp
                              const venue = it.Venue || it.VenueCode || '—'
                              const device = it.EgmHouseNo || it.TerminalID || '—'
                              const cashIn = it.CashIn ?? (isSession ? null : it.TransactionAmount)
                              const cashOut = it.CashOut ?? null
                              const turnover = it.Turnover ?? it.TotalWagered ?? (isSession ? it.CreditWin : null)
                              return (
                                <tr key={it.TransactionPK || it.SessionPK || idx} className="border-b border-gray-100">
                                  <td className="py-2">{isSession ? 'Session' : 'Transaction'}</td>
                                  <td className="py-2">{date ? new Date(date).toLocaleString() : '—'}</td>
                                  <td className="py-2">{venue}</td>
                                  <td className="py-2 font-mono text-xs">{device}</td>
                                  <td className="py-2 text-right">{cashIn != null ? `$${Number(cashIn).toLocaleString()}` : '—'}</td>
                                  <td className="py-2 text-right">{cashOut != null ? `$${Number(cashOut).toLocaleString()}` : '—'}</td>
                                  <td className="py-2 text-right">{turnover != null ? `$${Number(turnover).toLocaleString()}` : '—'}</td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                        {previewEvidence.items.length > 30 && <div className="text-xs text-gray-500 mt-2">Showing 30 of {previewEvidence.items.length} items</div>}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t flex items-center justify-between bg-gray-50 flex-shrink-0">
              <div className="text-xs text-gray-500">Filed SMR — Case dashboard is for active cases in progress.</div>
              <button className="btn-secondary" onClick={closePreview}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

